import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import {
  Pie,
  ResponsiveContainer,
  Sector,
  PieChart,
  Cell,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { BodyText, CaptionText, FlexRow } from '@make-software/cspr-design';
import { ChartTooltip } from '../chart-tooltip/chart-tooltip';

export const customPieColorsByIndex = {
  0: 'contentLightBlue',
  1: 'contentGreen',
  2: 'contentYellow',
};

export type SectorDataFormatType = {
  sectorKey: string;
  label: string;
  value: number;
  themeColor: string;
};

export type PieComponentDataFormatType = {
  total: number;
  totalLabel: string;
  chartData: SectorDataFormatType[];
};

export interface PieChartComponentProps {
  data: PieComponentDataFormatType;
  formatter: (value: any) => string;
  title?: string;
  description?: string;
}

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 5}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value; name; payload }>;
  name?: string;
  formatter: (value: any) => string;
}

const TooltipText = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    fontWeight: theme.typography.fontWeight.medium,
  })
);

const CustomTooltip = ({ active, payload, formatter }: CustomTooltipProps) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    return (
      <ChartTooltip>
        <CaptionText size={2} variation="darkGray">
          <Trans t={t}>{payload[0].name}</Trans>
        </CaptionText>
        <br />
        <FlexRow itemsSpacing={4}>
          <TooltipText size={1} noWrap scale={'lg'}>
            {formatter(payload[0].value)}
          </TooltipText>{' '}
          <TooltipText size={1} variation="darkGray" noWrap scale={'lg'}>
            &middot; {payload[0].payload.pct}%
          </TooltipText>
        </FlexRow>
      </ChartTooltip>
    );
  }

  return null;
};

export const PieChartComponent = (props: PieChartComponentProps) => {
  const { data, formatter } = props;

  const [activeIndex, setActiveIndex] = useState(undefined);

  const onMouseOver = useCallback((data, index) => {
    setActiveIndex(index);
  }, []);

  const onMouseLeave = useCallback((data, index) => {
    setActiveIndex(undefined);
  }, []);

  const calculatePercentage = (value, total) =>
    ((value / total) * 100).toFixed(2);

  const parsedData = data.chartData.map((item, idx) => ({
    ...item,
    pct: calculatePercentage(item.value, data.total),
  }));

  return (
    <ResponsiveContainer width={180} height={180}>
      <PieChart height={150} title={props.title} desc={props.description}>
        <Pie
          activeIndex={activeIndex}
          data={parsedData}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius={75}
          activeShape={renderActiveShape}
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
        >
          {parsedData.map((item) => (
            <Cell fill={item.themeColor} key={item.sectorKey} />
          ))}
        </Pie>
        <RechartsTooltip
          cursor={true}
          content={<CustomTooltip formatter={formatter} />}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};
